import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWhatsAppNumbers } from '@/hooks/useWhatsAppNumbers';
import { useWhatsAppTemplates } from '@/hooks/useWhatsAppTemplates';
import { useWorkflowTemplates } from '@/hooks/useWorkflowTemplates';

import { TemplateCodeEditor } from '../TemplateCodeEditor';
import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import { SendLaterFields } from './SendLaterFields';
import { WhatsAppFallbackFields } from './WhatsAppFallbackFields';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { TemplateComponent } from '@/lib/api/whatsapp-templates.api';
import type { SendSmsNodeData } from '@repo/shared-types';

/** Extract `{{1}}`, `{{2}}`, ... placeholders from a WhatsApp template body */
const parseWaVariables = (text: string): number[] => {
  const matches = text.match(/\{\{\d+\}\}/g) ?? [];
  return [...new Set(matches.map((m) => Number(m.replace(/[{}]/g, ''))))].sort((a, b) => a - b);
};

interface SendSmsConfigProps {
  data: SendSmsNodeData;
  onChange: (data: SendSmsNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const SendSmsConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: SendSmsConfigProps) => {
  const { data: templates } = useWorkflowTemplates();
  const [editorOpen, setEditorOpen] = useState(false);

  const update = (patch: Partial<SendSmsNodeData>) => onChange({ ...data, ...patch });

  const smsTemplates = templates?.filter((t) => t.type === 'sms') ?? [];

  const handleTemplateSelect = (id: string) => {
    const tpl = smsTemplates.find((t) => t.id === id);
    if (tpl) {
      update({ templateId: id, body: tpl.body });
    }
  };

  // --- WhatsApp template logic ---
  const isWhatsApp = data.provider === 'whatsapp';
  const { data: waNumbers } = useWhatsAppNumbers();
  const { data: waTemplates } = useWhatsAppTemplates();
  const approvedWaTemplates = useMemo(
    () => waTemplates?.filter((t) => t.status === 'APPROVED') ?? [],
    [waTemplates],
  );

  const selectedWaTemplate = useMemo(
    () =>
      approvedWaTemplates.find(
        (t) => t.name === data.waTemplateName && t.language === data.waTemplateLanguage,
      ),
    [approvedWaTemplates, data.waTemplateName, data.waTemplateLanguage],
  );

  const waComponentTexts = useMemo(() => {
    if (!selectedWaTemplate) return { body: '', header: '', buttonUrls: [] as string[] };
    const components = selectedWaTemplate.components as TemplateComponent[];
    const body = components?.find((c) => c.type === 'BODY')?.text ?? '';
    const header = components?.find((c) => c.type === 'HEADER' && c.format === 'TEXT')?.text ?? '';
    const buttons = components?.find((c) => c.type === 'BUTTONS')?.buttons ?? [];
    const buttonUrls = buttons.filter((b) => b.type === 'URL' && b.url).map((b) => b.url as string);
    return { body, header, buttonUrls };
  }, [selectedWaTemplate]);

  const waBodyText = waComponentTexts.body;
  const waVariableSlots = useMemo(() => parseWaVariables(waBodyText), [waBodyText]);
  const waHeaderVariableSlots = useMemo(
    () => parseWaVariables(waComponentTexts.header),
    [waComponentTexts.header],
  );
  const waButtonVariableSlots = useMemo(
    () => parseWaVariables(waComponentTexts.buttonUrls.join(' ')),
    [waComponentTexts.buttonUrls],
  );

  const handleWaTemplateSelect = (key: string) => {
    const tpl = approvedWaTemplates.find((t) => `${t.name}::${t.language}` === key);
    if (tpl) {
      const components = tpl.components as TemplateComponent[];
      const body = components?.find((c) => c.type === 'BODY')?.text ?? '';
      const header =
        components?.find((c) => c.type === 'HEADER' && c.format === 'TEXT')?.text ?? '';
      const buttons = components?.find((c) => c.type === 'BUTTONS')?.buttons ?? [];
      const buttonUrlText = buttons
        .filter((b) => b.type === 'URL' && b.url)
        .map((b) => b.url as string)
        .join(' ');
      const bodySlots = parseWaVariables(body);
      const headerSlots = parseWaVariables(header);
      const buttonSlots = parseWaVariables(buttonUrlText);
      update({
        waTemplateName: tpl.name,
        waTemplateLanguage: tpl.language,
        waTemplateVariables: bodySlots.map((idx) => data.waTemplateVariables?.[idx - 1] ?? ''),
        waTemplateHeaderVariables: headerSlots.map(
          (idx) => data.waTemplateHeaderVariables?.[idx - 1] ?? '',
        ),
        waTemplateButtonVariables: buttonSlots.map(
          (idx) => data.waTemplateButtonVariables?.[idx - 1] ?? '',
        ),
        templateId: null,
        body: null,
      });
    }
  };

  const updateWaVariable = (index: number, value: string) => {
    const vars = [...(data.waTemplateVariables ?? [])];
    vars[index] = value;
    update({ waTemplateVariables: vars });
  };

  const updateWaHeaderVariable = (index: number, value: string) => {
    const vars = [...(data.waTemplateHeaderVariables ?? [])];
    vars[index] = value;
    update({ waTemplateHeaderVariables: vars });
  };

  const updateWaButton = (index: number, value: string) => {
    const buttons = [0, 1, 2].map((i) => data.waButtons?.[i] ?? '');
    buttons[index] = value;
    // Drop trailing empty slots so the saved list only holds real buttons.
    while (buttons.length > 0 && !buttons[buttons.length - 1]) buttons.pop();
    update({ waButtons: buttons });
  };

  const updateWaButtonVariable = (index: number, value: string) => {
    const vars = [...(data.waTemplateButtonVariables ?? [])];
    vars[index] = value;
    update({ waTemplateButtonVariables: vars });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <VariableAutocomplete
          value={data.label}
          onChange={(v) => update({ label: v })}
          placeholder="Node label"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          analyticsNodeMetas={analyticsNodeMetas}
        />
      </div>

      {isWhatsApp ? (
        <>
          <div className="space-y-1">
            <label className="text-muted-foreground text-xs font-medium">From Number</label>
            <Select
              value={data.waFromNumberId ?? ''}
              onValueChange={(v) => {
                const num = waNumbers?.find((n) => n.whatsapp_number_id === v);
                update({
                  waFromNumberId: v,
                  from: num?.phone_number ?? undefined,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select WhatsApp number..." />
              </SelectTrigger>
              <SelectContent>
                {waNumbers
                  ?.filter((n) => n.is_active)
                  .map((n) => (
                    <SelectItem key={n.id} value={n.whatsapp_number_id}>
                      {n.phone_number}
                      {n.friendly_name ? ` — ${n.friendly_name}` : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-2">
            <input
              id="sms-wa-from-conversation"
              type="checkbox"
              className="mt-0.5"
              checked={data.waFromConversation ?? false}
              onChange={(e) => update({ waFromConversation: e.target.checked || undefined })}
            />
            <div>
              <label
                htmlFor="sms-wa-from-conversation"
                className="text-muted-foreground cursor-pointer text-xs font-medium"
              >
                Send from the conversation&apos;s number
              </label>
              <p className="text-muted-foreground text-[10px]">
                Uses the WhatsApp number of the conversation&apos;s configuration; falls back to the
                number above.
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="sms-wa-rate-limit"
              className="text-muted-foreground text-xs font-medium"
            >
              Max WhatsApp sends per hour
            </label>
            <Input
              id="sms-wa-rate-limit"
              type="number"
              min={1}
              step={1}
              value={data.waRateLimitPerHour ?? ''}
              placeholder="No limit"
              onChange={(e) => {
                const n = Number(e.target.value);
                update({
                  waRateLimitPerHour:
                    e.target.value !== '' && Number.isInteger(n) && n > 0 ? n : undefined,
                });
              }}
            />
            <p className="text-muted-foreground text-[10px]">
              Shared by every workflow sending from this number. Over the limit, messages are queued
              for the next hour.
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-muted-foreground text-xs font-medium">Message Type</label>
            <Select
              value={data.waMessageType ?? 'template'}
              onValueChange={(v) => {
                const type = v as NonNullable<SendSmsNodeData['waMessageType']>;
                update({
                  waMessageType: type,
                  waSmsFallback: type === 'template' ? data.waSmsFallback : undefined,
                  waButtons:
                    type === 'buttons' && !data.waButtons?.length ? ['Yes', 'No'] : data.waButtons,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="template">Template</SelectItem>
                <SelectItem value="contacts">Contact card</SelectItem>
                <SelectItem value="buttons">Reply buttons</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {data.waMessageType === 'buttons' ? (
            <>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-muted-foreground text-xs font-medium">Message</label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setEditorOpen(true)}
                  >
                    Edit Message
                  </Button>
                </div>
                {data.body ? (
                  <div
                    className="border-input bg-muted/30 max-h-32 cursor-pointer overflow-hidden rounded-md border p-2 text-xs"
                    onClick={() => setEditorOpen(true)}
                  >
                    <p className="text-muted-foreground line-clamp-5 font-mono whitespace-pre-line">
                      {data.body}
                    </p>
                  </div>
                ) : (
                  <div
                    className="border-input text-muted-foreground cursor-pointer rounded-md border border-dashed p-4 text-center text-xs"
                    onClick={() => setEditorOpen(true)}
                  >
                    Click to write the message shown above the buttons...
                  </div>
                )}
              </div>

              <TemplateCodeEditor
                value={data.body ?? ''}
                onChange={(v) => update({ body: v })}
                type="sms"
                reviewSchema={responseFieldsSchema}
                reviewNodeMetas={reviewNodeMetas}
                classifierNodeMetas={classifierNodeMetas}
                qualityNodeMetas={qualityNodeMetas}
                agentNodeMetas={agentNodeMetas}
                analyticsNodeMetas={analyticsNodeMetas}
                open={editorOpen}
                onOpenChange={setEditorOpen}
              />

              <div className="space-y-2">
                <label className="text-muted-foreground text-xs font-medium">Buttons</label>
                {[0, 1, 2].map((i) => (
                  <input
                    key={i}
                    aria-label={`Button ${i + 1}`}
                    className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
                    value={data.waButtons?.[i] ?? ''}
                    maxLength={20}
                    onChange={(e) => updateWaButton(i, e.target.value)}
                    placeholder={i === 0 ? 'Button 1 (e.g. Yes)' : `Button ${i + 1} (optional)`}
                  />
                ))}
                <p className="text-muted-foreground text-[10px]">
                  Up to 3 buttons, 20 characters each. A tap comes back into the chat as the
                  button&apos;s text, like a typed reply. Sent as a normal message, so it only
                  reaches the contact within 24 hours of their last message; otherwise the node
                  takes its failed branch. Cannot be scheduled.
                </p>
              </div>
            </>
          ) : data.waMessageType === 'contacts' ? (
            <>
              <div className="space-y-1">
                <label className="text-muted-foreground text-xs font-medium">Contact Name</label>
                <VariableAutocomplete
                  value={data.waContactName ?? ''}
                  onChange={(v) => update({ waContactName: v || undefined })}
                  placeholder="e.g. Sales Team"
                  responseFieldsSchema={responseFieldsSchema}
                  reviewNodeMetas={reviewNodeMetas}
                  classifierNodeMetas={classifierNodeMetas}
                  qualityNodeMetas={qualityNodeMetas}
                  agentNodeMetas={agentNodeMetas}
                  analyticsNodeMetas={analyticsNodeMetas}
                />
              </div>
              <div className="space-y-1">
                <label className="text-muted-foreground text-xs font-medium">Contact Phone</label>
                <VariableAutocomplete
                  value={data.waContactPhone ?? ''}
                  onChange={(v) => update({ waContactPhone: v || undefined })}
                  placeholder="+447..."
                  responseFieldsSchema={responseFieldsSchema}
                  reviewNodeMetas={reviewNodeMetas}
                  classifierNodeMetas={classifierNodeMetas}
                  qualityNodeMetas={qualityNodeMetas}
                  agentNodeMetas={agentNodeMetas}
                  analyticsNodeMetas={analyticsNodeMetas}
                />
                <p className="text-muted-foreground text-[10px]">
                  The number shared as a tappable contact card in the chat
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-muted-foreground text-xs font-medium">
                    First name (optional)
                  </label>
                  <VariableAutocomplete
                    value={data.waContactFirstName ?? ''}
                    onChange={(v) => update({ waContactFirstName: v || undefined })}
                    placeholder="Derived from name"
                    responseFieldsSchema={responseFieldsSchema}
                    reviewNodeMetas={reviewNodeMetas}
                    classifierNodeMetas={classifierNodeMetas}
                    qualityNodeMetas={qualityNodeMetas}
                    agentNodeMetas={agentNodeMetas}
                    analyticsNodeMetas={analyticsNodeMetas}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-muted-foreground text-xs font-medium">
                    Last name (optional)
                  </label>
                  <VariableAutocomplete
                    value={data.waContactLastName ?? ''}
                    onChange={(v) => update({ waContactLastName: v || undefined })}
                    placeholder="Derived from name"
                    responseFieldsSchema={responseFieldsSchema}
                    reviewNodeMetas={reviewNodeMetas}
                    classifierNodeMetas={classifierNodeMetas}
                    qualityNodeMetas={qualityNodeMetas}
                    agentNodeMetas={agentNodeMetas}
                    analyticsNodeMetas={analyticsNodeMetas}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-muted-foreground text-xs font-medium">
                  Company (optional)
                </label>
                <VariableAutocomplete
                  value={data.waContactCompany ?? ''}
                  onChange={(v) => update({ waContactCompany: v || undefined })}
                  placeholder="Shown under the contact name"
                  responseFieldsSchema={responseFieldsSchema}
                  reviewNodeMetas={reviewNodeMetas}
                  classifierNodeMetas={classifierNodeMetas}
                  qualityNodeMetas={qualityNodeMetas}
                  agentNodeMetas={agentNodeMetas}
                  analyticsNodeMetas={analyticsNodeMetas}
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1">
                <label className="text-muted-foreground text-xs font-medium">
                  WhatsApp Template
                </label>
                <Select
                  value={
                    data.waTemplateName && data.waTemplateLanguage
                      ? `${data.waTemplateName}::${data.waTemplateLanguage}`
                      : ''
                  }
                  onValueChange={handleWaTemplateSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an approved template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {approvedWaTemplates.map((t) => (
                      <SelectItem key={t.id} value={`${t.name}::${t.language}`}>
                        {t.name} ({t.language})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedWaTemplate && (waBodyText || waComponentTexts.header) && (
                <div className="space-y-3">
                  <div className="border-input bg-muted/30 rounded-md border p-2 text-xs">
                    {waComponentTexts.header && (
                      <p className="text-muted-foreground mb-1 font-mono font-semibold">
                        {waComponentTexts.header}
                      </p>
                    )}
                    <p className="text-muted-foreground font-mono">{waBodyText}</p>
                    {waComponentTexts.buttonUrls.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {waComponentTexts.buttonUrls.map((url, i) => (
                          <p
                            key={i}
                            className="text-muted-foreground truncate font-mono text-[10px]"
                          >
                            Button URL: {url}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  {waHeaderVariableSlots.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-muted-foreground text-xs font-medium">
                        Header Variables
                      </label>
                      {waHeaderVariableSlots.map((slot) => (
                        <div key={`header-${slot}`} className="space-y-1">
                          <label className="text-muted-foreground text-[10px]">
                            Header {`{{${slot}}}`}
                          </label>
                          <div className="flex gap-1">
                            <VariableAutocomplete
                              value={data.waTemplateHeaderVariables?.[slot - 1] ?? ''}
                              onChange={(v) => updateWaHeaderVariable(slot - 1, v)}
                              placeholder={`Value for header {{${slot}}}`}
                              responseFieldsSchema={responseFieldsSchema}
                              reviewNodeMetas={reviewNodeMetas}
                              classifierNodeMetas={classifierNodeMetas}
                              qualityNodeMetas={qualityNodeMetas}
                              agentNodeMetas={agentNodeMetas}
                              analyticsNodeMetas={analyticsNodeMetas}
                              className="flex-1"
                            />
                            <VariablePicker
                              onSelect={(v) =>
                                updateWaHeaderVariable(
                                  slot - 1,
                                  (data.waTemplateHeaderVariables?.[slot - 1] ?? '') + v,
                                )
                              }
                              responseFieldsSchema={responseFieldsSchema}
                              reviewNodeMetas={reviewNodeMetas}
                              classifierNodeMetas={classifierNodeMetas}
                              qualityNodeMetas={qualityNodeMetas}
                              agentNodeMetas={agentNodeMetas}
                              analyticsNodeMetas={analyticsNodeMetas}
                              triggerType={triggerType}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {waVariableSlots.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-muted-foreground text-xs font-medium">
                        Body Variables
                      </label>
                      {waVariableSlots.map((slot) => (
                        <div key={`body-${slot}`} className="space-y-1">
                          <label className="text-muted-foreground text-[10px]">{`{{${slot}}}`}</label>
                          <div className="flex gap-1">
                            <VariableAutocomplete
                              value={data.waTemplateVariables?.[slot - 1] ?? ''}
                              onChange={(v) => updateWaVariable(slot - 1, v)}
                              placeholder={`Value for {{${slot}}}`}
                              responseFieldsSchema={responseFieldsSchema}
                              reviewNodeMetas={reviewNodeMetas}
                              classifierNodeMetas={classifierNodeMetas}
                              qualityNodeMetas={qualityNodeMetas}
                              agentNodeMetas={agentNodeMetas}
                              analyticsNodeMetas={analyticsNodeMetas}
                              className="flex-1"
                            />
                            <VariablePicker
                              onSelect={(v) =>
                                updateWaVariable(
                                  slot - 1,
                                  (data.waTemplateVariables?.[slot - 1] ?? '') + v,
                                )
                              }
                              responseFieldsSchema={responseFieldsSchema}
                              reviewNodeMetas={reviewNodeMetas}
                              classifierNodeMetas={classifierNodeMetas}
                              qualityNodeMetas={qualityNodeMetas}
                              agentNodeMetas={agentNodeMetas}
                              analyticsNodeMetas={analyticsNodeMetas}
                              triggerType={triggerType}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {waButtonVariableSlots.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-muted-foreground text-xs font-medium">
                        Button URL Variables
                      </label>
                      {waButtonVariableSlots.map((slot) => (
                        <div key={`btn-${slot}`} className="space-y-1">
                          <label className="text-muted-foreground text-[10px]">
                            Button URL {`{{${slot}}}`}
                          </label>
                          <div className="flex gap-1">
                            <VariableAutocomplete
                              value={data.waTemplateButtonVariables?.[slot - 1] ?? ''}
                              onChange={(v) => updateWaButtonVariable(slot - 1, v)}
                              placeholder={`Value for button {{${slot}}}`}
                              responseFieldsSchema={responseFieldsSchema}
                              reviewNodeMetas={reviewNodeMetas}
                              classifierNodeMetas={classifierNodeMetas}
                              qualityNodeMetas={qualityNodeMetas}
                              agentNodeMetas={agentNodeMetas}
                              analyticsNodeMetas={analyticsNodeMetas}
                              className="flex-1"
                            />
                            <VariablePicker
                              onSelect={(v) =>
                                updateWaButtonVariable(
                                  slot - 1,
                                  (data.waTemplateButtonVariables?.[slot - 1] ?? '') + v,
                                )
                              }
                              responseFieldsSchema={responseFieldsSchema}
                              reviewNodeMetas={reviewNodeMetas}
                              classifierNodeMetas={classifierNodeMetas}
                              qualityNodeMetas={qualityNodeMetas}
                              agentNodeMetas={agentNodeMetas}
                              analyticsNodeMetas={analyticsNodeMetas}
                              triggerType={triggerType}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {smsTemplates.length > 0 && (
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium">Template</label>
              <Select value={data.templateId ?? ''} onValueChange={handleTemplateSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Load from template..." />
                </SelectTrigger>
                <SelectContent>
                  {smsTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-muted-foreground text-xs font-medium">Body</label>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setEditorOpen(true)}
              >
                Edit Template
              </Button>
            </div>
            {data.body ? (
              <div
                className="border-input bg-muted/30 max-h-32 cursor-pointer overflow-hidden rounded-md border p-2 text-xs"
                onClick={() => setEditorOpen(true)}
              >
                <p className="text-muted-foreground line-clamp-5 font-mono">{data.body}</p>
              </div>
            ) : (
              <div
                className="border-input text-muted-foreground cursor-pointer rounded-md border border-dashed p-4 text-center text-xs"
                onClick={() => setEditorOpen(true)}
              >
                Click to edit template body...
              </div>
            )}
          </div>

          <TemplateCodeEditor
            value={data.body ?? ''}
            onChange={(v) => update({ body: v })}
            type="sms"
            reviewSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            open={editorOpen}
            onOpenChange={setEditorOpen}
          />
        </>
      )}

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">To</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.to}
            onChange={(v) => update({ to: v })}
            placeholder="{{contact.phone}}"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            className="flex-1"
          />
          <VariablePicker
            onSelect={(v) => update({ to: data.to + v })}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Default Country Code</label>
        <input
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
          value={((data as unknown as Record<string, unknown>).defaultCountryCode as string) ?? ''}
          onChange={(e) =>
            update({
              defaultCountryCode: e.target.value || undefined,
            } as unknown as Partial<SendSmsNodeData>)
          }
          placeholder="+44 (used when number starts with 0)"
        />
        <p className="text-muted-foreground text-[10px]">
          If the recipient phone starts with 0, this code replaces it (e.g. +44 turns 07xxx into
          +447xxx)
        </p>
      </div>

      <div className="border-t pt-4">
        <p className="text-muted-foreground mb-2 text-xs">
          Provider override — leave blank to inherit from the configuration tied to this
          call/campaign. Set explicitly for webhook-triggered workflows that have no call context.
        </p>

        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">Provider</label>
          <Select
            value={data.provider ?? 'inherit'}
            onValueChange={(v) =>
              update({
                provider: v === 'inherit' ? undefined : (v as 'twilio' | 'telnyx' | 'whatsapp'),
                waSmsFallback: v === 'whatsapp' ? data.waSmsFallback : undefined,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inherit">Inherit (default)</SelectItem>
              <SelectItem value="twilio">Twilio</SelectItem>
              <SelectItem value="telnyx">Telnyx</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-2 space-y-1">
          <label className="text-muted-foreground text-xs font-medium">
            From number (optional)
          </label>
          <VariableAutocomplete
            value={data.from ?? ''}
            onChange={(v) => update({ from: v || undefined })}
            placeholder="+447... (leave blank to use provider default)"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
          />
        </div>
      </div>

      <WhatsAppFallbackFields
        data={data}
        onChange={onChange}
        responseFieldsSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
      />

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Schedule</label>
        <SendLaterFields
          value={{
            sendAt: data.sendAt,
            offsetMinutes: data.offsetMinutes,
            bookingId: data.bookingId,
          }}
          onChange={(fields) => update(fields)}
          showBookingLink
          immediateHint="send immediately"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
        />
      </div>
    </div>
  );
};
